(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let frame = 0;
  let observer;
  let homeHeroObserver;
  let collectionItemObserver;
  let collectionMutationObserver;
  let lazyVideoObserver;

  const updateScrollState = () => {
    frame = 0;
    const scrollable = document.documentElement.scrollHeight - window.innerHeight;
    const progress = scrollable > 0 ? Math.min(1, Math.max(0, window.scrollY / scrollable)) : 0;

    document.documentElement.style.setProperty('--smellology-scroll-progress', progress.toFixed(4));
    document.body.classList.toggle('smellology-has-scrolled', window.scrollY > 16);
  };

  const requestScrollUpdate = () => {
    if (frame) return;
    frame = window.requestAnimationFrame(updateScrollState);
  };

  const prepareSections = () => {
    observer?.disconnect();
    document.documentElement.classList.remove('smellology-motion-ready');

    const sections = [...document.querySelectorAll('#MainContent > .shopify-section')];
    sections.forEach((section) => section.classList.remove('smellology-reveal', 'is-visible'));

    if (reduceMotion.matches || !('IntersectionObserver' in window)) {
      sections.forEach((section) => section.classList.add('is-visible'));
      return;
    }

    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );

    sections.forEach((section, index) => {
      if (index === 0 || section.getBoundingClientRect().top < window.innerHeight * 0.92) {
        section.classList.add('is-visible');
        return;
      }

      section.classList.add('smellology-reveal');
      observer.observe(section);
    });

    document.documentElement.classList.add('smellology-motion-ready');
  };

  const prepareHomepage = () => {
    const main = document.querySelector("#MainContent[data-template='index']");
    if (!main) return;

    const heroes = [...main.querySelectorAll('.hero')];
    if (!heroes.length) return;

    homeHeroObserver?.disconnect();
    homeHeroObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-content-visible');
          homeHeroObserver.unobserve(entry.target);
        });
      },
      { threshold: 0.22 }
    );

    heroes.forEach((hero) => {
      if (hero.classList.contains('smellology-home-hero')) return;
      hero.classList.add('smellology-home-hero');
      homeHeroObserver.observe(hero);

      if (!reduceMotion.matches && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        hero.addEventListener('pointermove', (event) => {
          const rect = hero.getBoundingClientRect();
          const x = ((event.clientX - rect.left) / rect.width - 0.5) * -10;
          const y = ((event.clientY - rect.top) / rect.height - 0.5) * -8;
          hero.classList.add('is-pointer-active');
          hero.style.setProperty('--smellology-hero-x', `${x.toFixed(2)}px`);
          hero.style.setProperty('--smellology-hero-y', `${y.toFixed(2)}px`);
        }, { passive: true });

        hero.addEventListener('pointerleave', () => {
          hero.classList.remove('is-pointer-active');
          hero.style.setProperty('--smellology-hero-x', '0px');
          hero.style.setProperty('--smellology-hero-y', '0px');
        }, { passive: true });
      }
    });

    const firstHero = heroes[0];
    if (!firstHero.querySelector('.smellology-scroll-cue')) {
      const cue = document.createElement('button');
      cue.type = 'button';
      cue.className = 'smellology-scroll-cue';
      cue.textContent = 'Explore';
      cue.setAttribute('aria-label', 'Explore the next section');
      cue.addEventListener('click', () => {
        const nextSection = firstHero.closest('.shopify-section')?.nextElementSibling;
        nextSection?.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'start' });
      });
      firstHero.append(cue);
    }
  };

  const prepareLazyVideos = () => {
    lazyVideoObserver?.disconnect();

    const videos = [...document.querySelectorAll('video[data-smellology-lazy-video]')];
    if (!videos.length) return;

    if (!('IntersectionObserver' in window)) {
      videos.forEach((video) => {
        video.preload = 'metadata';
        video.load();
      });
      return;
    }

    lazyVideoObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;

          if (entry.isIntersecting) {
            if (!video.dataset.smellologyLoaded) {
              video.dataset.smellologyLoaded = 'true';
              video.preload = 'auto';
              video.load();
            }

            if (!reduceMotion.matches) {
              video.play()?.catch(() => {});
            }
          } else if (!video.paused) {
            video.pause();
          }
        });
      },
      { rootMargin: '500px 0px', threshold: 0.01 }
    );

    videos.forEach((video) => lazyVideoObserver.observe(video));
  };

  const prepareProductPage = () => {
    const main = document.querySelector("#MainContent[data-template^='product']");
    if (!main || main.dataset.smellologyProductEnhanced === 'true') return;

    main.dataset.smellologyProductEnhanced = 'true';
    window.requestAnimationFrame(() => main.classList.add('smellology-product-ready'));

    main.addEventListener('change', (event) => {
      const input = event.target.closest('.variant-option input');
      if (!input || reduceMotion.matches) return;

      const label = input.closest('.variant-option__button-label');
      if (!label) return;

      label.classList.remove('smellology-choice-pulse');
      window.requestAnimationFrame(() => label.classList.add('smellology-choice-pulse'));
      label.addEventListener('animationend', () => label.classList.remove('smellology-choice-pulse'), { once: true });
    });
  };

  const prepareCollectionPage = () => {
    const main = document.querySelector("#MainContent[data-template^='collection']");
    if (!main) return;

    const grid = main.querySelector('.product-grid');
    if (!grid) return;

    if (!collectionItemObserver && !reduceMotion.matches && 'IntersectionObserver' in window) {
      collectionItemObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-visible');
            collectionItemObserver.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -5% 0px', threshold: 0.08 }
      );
    }

    const enhanceItems = () => {
      const items = [...main.querySelectorAll('.product-grid__item:not(.smellology-grid-item)')];
      items.forEach((item, index) => {
        item.classList.add('smellology-grid-item');
        item.style.setProperty('--smellology-grid-index', String(index % 8));

        if (reduceMotion.matches || !collectionItemObserver) {
          item.classList.add('is-visible');
        } else {
          collectionItemObserver.observe(item);
        }
      });
      main.classList.remove('smellology-collection-filtering');
    };

    enhanceItems();

    if (main.dataset.smellologyCollectionEnhanced === 'true') return;
    main.dataset.smellologyCollectionEnhanced = 'true';

    main.addEventListener('change', (event) => {
      if (event.target.closest('facets-form-component, sorting-filter-component')) {
        main.classList.add('smellology-collection-filtering');
      }
    });

    collectionMutationObserver?.disconnect();
    collectionMutationObserver = new MutationObserver(enhanceItems);
    collectionMutationObserver.observe(main, { childList: true, subtree: true });
  };

  const init = () => {
    if (!document.querySelector('.smellology-scroll-progress')) {
      const progress = document.createElement('div');
      progress.className = 'smellology-scroll-progress';
      progress.setAttribute('aria-hidden', 'true');
      document.body.prepend(progress);
    }

    prepareSections();
    prepareHomepage();
    prepareLazyVideos();
    prepareProductPage();
    prepareCollectionPage();
    updateScrollState();
  };

  window.addEventListener('scroll', requestScrollUpdate, { passive: true });
  window.addEventListener('resize', requestScrollUpdate, { passive: true });
  reduceMotion.addEventListener('change', prepareSections);
  document.addEventListener('shopify:section:load', prepareSections);
  document.addEventListener('shopify:section:load', prepareHomepage);
  document.addEventListener('shopify:section:load', prepareLazyVideos);
  document.addEventListener('shopify:section:load', prepareProductPage);
  document.addEventListener('shopify:section:load', prepareCollectionPage);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
